/**
 * #661 (F13) — the status, the code and the params of a refused request.
 *
 * The backend answers a refusal as `{ error, code, params }`. A component that
 * branches on it reads the answer here, never with its own cast of
 * `err.response.data`. Before this module, 22 sites wrote that cast, in four
 * shapes, and a change of the answer shape needed 22 edits that no type check
 * found.
 *
 * This file is a leaf: it imports `axios` only. `observability.ts` and
 * `api/client.ts` read the same shape for the telemetry, and `hooks/useApiCall.ts`
 * imports `observability.ts`, so a helper in that hook file could not be
 * imported back without a cycle.
 *
 * Rule: branch on the `code` or on the status of a refusal, never on its text
 * (#559). `frontend-code-parity-559.spec.ts` reads each `code === 'X'`.
 */
import axios from 'axios';

/** The values that the backend puts into a translated message. */
export type MessageParams = Record<string, string | number | null | undefined>;

/** The answer of the server to a request that it refused. */
export interface Refusal {
  status: number;
  /** The stable identifier of the refusal. Never an empty string. */
  code?: string;
  params?: MessageParams;
  /** The English text of the backend: a fallback, not a text to branch on. */
  error?: string;
}

/**
 * The answer of the server, or `undefined` when there is none: the network, a
 * timeout, or a bug of the code.
 */
export function refusalOf(err: unknown): Refusal | undefined {
  if (!axios.isAxiosError(err) || !err.response) return undefined;
  const { status, data } = err.response;
  const body = data && typeof data === 'object'
    ? data as { code?: unknown; params?: unknown; error?: unknown }
    : undefined;
  return {
    status,
    code: typeof body?.code === 'string' && body.code ? body.code : undefined,
    params: body?.params && typeof body.params === 'object' ? body.params as MessageParams : undefined,
    error: typeof body?.error === 'string' ? body.error : undefined,
  };
}

/** The HTTP status of the answer, or `undefined` when the server did not answer. */
export function errorStatus(err: unknown): number | undefined {
  return refusalOf(err)?.status;
}

/** The `code` of the answer, or `undefined`. */
export function errorCode(err: unknown): string | undefined {
  return refusalOf(err)?.code;
}

/**
 * The item of a detail page does not exist: a 404, or a 400 `INVALID_UUID`
 * (a cut or mistyped id in the address). Any other failure is a failed load
 * that a retry can fix, so it shows `ErrorState` (#457, #595).
 */
export function isNotFound(err: unknown): boolean {
  const r = refusalOf(err);
  return r?.status === 404 || (r?.status === 400 && r.code === 'INVALID_UUID');
}
